import {useMemo} from "react";
import useGetUserInfo from "@/hooks/useGetUserInfo";
import {useAppConfig} from "@/store/useAppConfig.store";
import {useAuthStore} from "@/store/useAuth.store";
import {useMiniGames} from "@/store/useMiniGames";
import {getUserAvatarImage, getUserInitials} from "@/util/avatarUtils";
import {AUTH_ONLY_STORIES, AYG_PROMO_STORIES, getProviderLogo} from "../stories.constants";

/**
 * Core hook for stories logic.
 *
 * Responsibilities:
 * - Detects authentication state
 * - Builds the correct story list per auth status
 * - Tracks viewed stories (in-memory)
 * - Limits guest stories to GUEST_MAX_STORIES
 * - Injects locked gate story for guests
 *
 * @param {Set<string>} viewedIds - externally tracked viewed story IDs
 * @returns {{
 *   stories: import("../stories.constants").Story[],
 *   isAuthenticated: boolean,
 *   showGate: boolean,
 *   gateIndex: number,
 *   userData: object | undefined,
 *   userInitials: string,
 *   avatarImage: string | null,
 *   displayName: string,
 *   providerKeys: string[],
 * }}
 */
const useStories = (viewedIds = new Set()) => {
  const authStatus = useAuthStore(state => state.authStatus);
  const providerOptions = useMiniGames(state => state.providerOptions);
  const storyAds = useAppConfig(state => state.storyAds);
  const {data: userData} = useGetUserInfo();

  const isAuthenticated = authStatus === "authenticated";

  const userInitials = getUserInitials(userData);
  const avatarImage = getUserAvatarImage(userData);
  const displayName = userData?.given_name || userData?.firstName || userData?.email?.split("@")[0] || "Mi perfil";

  /** Build ad stories from dashboard-uploaded banners (1080×1920) */
  const adStories = useMemo(() => {
    if (!storyAds || storyAds.length === 0) return [];
    const CDN = "https://images.cdn.raspaygana.com.ve/images";
    return storyAds.map((ad, i) => ({
      id: `story-ad-${ad.id || i}`,
      label: ad.title || "Publicidad",
      icon: `${CDN}/${ad.ID}`,
      type: /** @type {const} */ ("promotion"),
      ringVariant: "promotion",
    }));
  }, [storyAds]);

  /** Build provider stories from store data */
  const providerStories = useMemo(() => {
    const ordered = [
      "pragmatic",
      "bgaming",
      "evoplay",
      "hacksaw",
      "evolution",
      "pixmove",
      "upgaming",
      "endorphina",
      "betby",
      "tada",
      "gamingcorps",
      "kagaming",
    ];
    return ordered
      .filter(key => {
        const opt = providerOptions.find(o => o.value === key || o.value === `${key}gaming`);
        return opt && getProviderLogo(key);
      })
      .map(key => ({
        id: `provider-${key}`,
        label: key.charAt(0).toUpperCase() + key.slice(1),
        icon: getProviderLogo(key),
        type: /** @type {const} */ ("provider"),
        providerKey: key,
        ringVariant: "provider",
      }));
  }, [providerOptions]);

  /** Active provider keys for top-games fetching */
  const providerKeys = useMemo(() => providerStories.map(s => s.providerKey), [providerStories]);

  /** Build final story list depending on auth state */
  const stories = useMemo(() => {
    if (isAuthenticated) {
      /** @type {import("../stories.constants").Story} */
      const userStory = {
        id: "user-profile",
        label: displayName,
        icon: avatarImage || "",
        type: "user",
        ringVariant: "profile",
      };

      /** @type {import("../stories.constants").Story} */
      const communityStory = {
        id: "community",
        label: "Comunidad",
        icon: "",
        type: "community",
        ringVariant: "community",
      };

      return [userStory, communityStory, ...AYG_PROMO_STORIES, ...adStories, ...AUTH_ONLY_STORIES, ...providerStories];
    }

    // Guest: AYG promos + ad stories + provider stories (no user/community/auth-only)
    return [...AYG_PROMO_STORIES, ...adStories, ...providerStories];
  }, [isAuthenticated, displayName, avatarImage, providerStories, adStories]);

  /** Apply viewed state */
  const storiesWithViewState = useMemo(
    () =>
      stories.map(s => ({
        ...s,
        viewed: viewedIds.has(s.id),
      })),
    [stories, viewedIds]
  );

  /** Gate logic for guests */
  const showGate = !isAuthenticated;

  /** Visible stories */
  const visibleStories = storiesWithViewState;

  return {
    stories: visibleStories,
    allStories: storiesWithViewState,
    isAuthenticated,
    showGate,
    userData,
    userInitials,
    avatarImage,
    displayName,
    providerKeys,
  };
};

export default useStories;
